
























































































import Vue from 'vue';
import JobProvider from '@/api/JobProvider';
import SkillProvider, { SKILL_SEARCH_CAP, ISkillSearchParams } from '@/api/SkillProvider';
import {
    ISkillBrowseFilter, SkillSortKey, emptySkillFilter,
    skillFilterToQuery, queryToSkillFilter, isSkillFilterActive,
} from '@/util/skillBrowseQuery';
import { SortDir, defaultDir } from '@/util/browseQuery';
import { buildJobIndex, withAncestors, IJobIndex } from '@/util/skillJobFilter';
import { Parent, IFilterChip, buildChips } from '@/util/filterChips';
import { canonicalQuery } from '@/util/queryParams';
import ResponsiveCardList from "@/components/util/ResponsiveCardList.vue";
import ResponsiveCardListEntry from "@/components/util/ResponsiveCardListEntry.vue";
import SkillStubLink  from "@/components/skill/SkillStubLink.vue";
import DvBrowseFrame from "@/components/util/DvBrowseFrame.vue";
import DvStagger from "@/components/util/DvStagger.vue";
import SkillSearchClassSelector from "@/components/skill/SkillSearchClassSelector.vue";
import SkillSearchTypeSelector from "@/components/skill/SkillSearchTypeSelector.vue";
import SkillSearchBehaviourSelector from "@/components/skill/SkillSearchBehaviourSelector.vue";
import SkillSearchWeaponSelector from "@/components/skill/SkillSearchWeaponSelector.vue";
import SkillSearchGroupFilter from "@/components/skill/SkillSearchGroupFilter.vue";
import {
    skillTypeName, durationTypeName, targetTypeName, elementInfo,
} from '@/helpers/SkillEnumNames';
import { WeaponTypeName } from '@/models/items/ItemEnums';
import { SkillType, SkillDurationType, SkillTargetType } from '@/models/skills/SkillEnums';
import { ElementalType } from '@/models/common/ElementalType';
import ISkill from '../../models/skills/ISkill';

//  Mirrors DvBrowseFrame.vue's own (unimported) BrowseState -- every other
//  .vue-to-.vue import in this codebase pulls only the default export, so
//  this small shape is duplicated locally rather than betting on named-export
//  resolution through the *.vue ambient shim. See ItemSearchEx.vue.
type BrowseState = 'prompt' | 'loading' | 'results' | 'empty' | 'error' | 'end';

//  Every filter field that is an id list, so a single handler can service
//  every selector and every chip removal.
type SkillFilterListKey =
    'jobs' | 'skillTypes' | 'durationTypes' | 'targetTypes' | 'elements'
    | 'weapons' | 'groups' | 'skillGroups' | 'globalSkillGroups';

//  Skill search accepts either a name or a raw skill id, so a single
//  character is a legitimate query -- unlike item search, whose 1-char
//  cliff (97k rows / 74.8 MB / 20.3s) is specific to the item table.
//  Measured empirically against the live `duck` region: a 1-char skill
//  name search ('c') returned in well under a second with a small payload.
//  See task-8-9-report.md for the exact numbers.
const MIN_LENGTH = 1;

interface ISortOption {
    key: SkillSortKey;
    label: string;
}

//  Sort key for the `name` sort. A skill with _NameID 0 renders as "[MID:0]",
//  so sorting those on their message would pile every nameless skill together
//  under one meaningless string; internalName at least clusters them usefully.
function sortName(s: ISkill): string {
    if (s.name && s.name.id !== 0) {
        return s.name.message;
    }
    return s.internalName || `Skill ${s.id}`;
}

interface IData {
    filter: ISkillBrowseFilter;
    loading: boolean;
    error: string;
    skills: ISkill[];
    //  Guards against a stale response clobbering a newer one: the frame
    //  debounces input, but a fast retype can still leave two requests in the
    //  air at once.
    loadSeq: number;
    //  Canonical serialization of the last query WE pushed, so the $route
    //  watcher can ignore its own echo.
    lastPushedQuery: string;
    jobIndex: IJobIndex;
    //  Resolved once the job hierarchy has loaded. Awaited only when a job
    //  filter with inherit-on actually needs ancestors, so a plain name search
    //  never waits on it.
    jobsReady: Promise<void> | null;
    jobParents: Parent[];
    jobLabels: Record<number, string>;
}

export default Vue.extend({
    components: {
        DvBrowseFrame,
        DvStagger,
        ResponsiveCardList,
        ResponsiveCardListEntry,
        SkillStubLink,
        SkillSearchClassSelector,
        SkillSearchTypeSelector,
        SkillSearchBehaviourSelector,
        SkillSearchWeaponSelector,
        SkillSearchGroupFilter,
    },
    data(): IData {
        return {
            filter: emptySkillFilter(),
            loading: false,
            error: '',
            skills: [],
            loadSeq: 0,
            lastPushedQuery: '',
            jobIndex: { parentOf: {}, childrenOf: {} },
            jobsReady: null,
            jobParents: [],
            jobLabels: {},
        };
    },
    //  Seeded in created(), not mounted(): Vue mounts CHILDREN before the
    //  parent's own mounted() hook, and the filter selectors added in later
    //  tasks emit on mount. Seeding here runs before any child is even
    //  instantiated, so a child's first emit can never race the URL restore
    //  and blank out q=. Same reasoning as ItemSearchEx, which was bitten by
    //  exactly this.
    created() {
        this.filter = queryToSkillFilter(
            this.$route.query as Record<string, string | string[] | undefined>);
        this.lastPushedQuery = canonicalQuery(skillFilterToQuery(this.filter));
        this.jobsReady = this.loadJobs();
        this.search();
    },
    watch: {
        //  Reacts to genuine external navigation (back/forward, a pasted link)
        //  without double-searching on our own router.replace calls.
        $route(to) {
            const incoming = canonicalQuery(to.query as Record<string, any>);
            if (incoming === this.lastPushedQuery) {
                return;
            }
            this.filter = queryToSkillFilter(
                to.query as Record<string, string | string[] | undefined>);
            this.lastPushedQuery = incoming;
            this.search();
        },
    },
    computed: {
        minLength(): number {
            return MIN_LENGTH;
        },
        //  A filter-only query is valid: /skills/search 400s only when BOTH
        //  the name and every filter are absent.
        hasQuery(): boolean {
            return this.filter.name.trim().length >= MIN_LENGTH
                || isSkillFilterActive(this.filter);
        },
        state(): BrowseState {
            if (!this.hasQuery) {
                return 'prompt';
            }
            if (this.loading) {
                return 'loading';
            }
            if (this.error) {
                return 'error';
            }
            return this.skills.length === 0 ? 'empty' : 'results';
        },
        truncated(): boolean {
            return this.skills.length >= SKILL_SEARCH_CAP;
        },
        sorts(): ISortOption[] {
            return [
                { key: 'relevance', label: 'Relevance' },
                { key: 'name', label: 'Name' },
                { key: 'id', label: 'ID' },
            ];
        },
        //  Client-side: the response is at most SKILL_SEARCH_CAP rows and the
        //  endpoint has no sort param. 'relevance' is the server's own order,
        //  so it is returned untouched rather than sorted by a proxy key.
        sortedSkills(): ISkill[] {
            const f = this.filter;
            if (f.sort === 'relevance') {
                return this.skills;
            }
            const mul = f.dir === 'asc' ? 1 : -1;
            return [...this.skills].sort((a, b) => {
                if (f.sort === 'id') {
                    return (a.id - b.id) * mul;
                }
                return sortName(a).localeCompare(sortName(b)) * mul;
            });
        },
        elementEntries(): { id: number; name: string; cssClass: string; iconClass: string }[] {
            return [
                ElementalType.NONE,
                ElementalType.FIRE,
                ElementalType.WATER,
                ElementalType.LIGHT,
                ElementalType.DARK,
            ].map((id) => ({ id, ...elementInfo(id) }));
        },
        techMode(): boolean {
            return this.$store.state.techMode;
        },
        chips(): IFilterChip[] {
            const f = this.filter;
            const chips = buildChips([
                { group: 'jobs', selected: f.jobs, parents: this.jobParents,
                  label: (id) => this.jobLabels[id] ?? `Job ${id}` },
                { group: 'skillTypes', selected: f.skillTypes, parents: [],
                  label: (id) => skillTypeName(id as SkillType) },
                { group: 'durationTypes', selected: f.durationTypes, parents: [],
                  label: (id) => durationTypeName(id as SkillDurationType) },
                { group: 'targetTypes', selected: f.targetTypes, parents: [],
                  label: (id) => targetTypeName(id as SkillTargetType) },
                { group: 'elements', selected: f.elements, parents: [],
                  label: (id) => elementInfo(id as ElementalType).name },
                { group: 'weapons', selected: f.weapons, parents: [],
                  label: (id) => (id === -1
                      ? 'No weapon'
                      : (WeaponTypeName as Record<number, string>)[id] ?? `Weapon ${id}`) },
                { group: 'groups', selected: f.groups, parents: [],
                  label: (id) => `Group ${id}` },
                { group: 'skillGroups', selected: f.skillGroups, parents: [],
                  label: (id) => `Skill group ${id}` },
                { group: 'globalSkillGroups', selected: f.globalSkillGroups, parents: [],
                  label: (id) => `Global group ${id}` },
            ]);
            //  Appended, not built by buildChips: this is a modifier, not an
            //  id selection. It appears ONLY when inherit is off, because an
            //  inh=0 URL would otherwise apply an invisible constraint.
            if (!f.inheritJobs) {
                chips.push({ group: 'inherit', label: 'Exact class only', removeIds: [] });
            }
            return chips;
        },
    },
    methods: {
        //  Duplicates SkillSearchClassSelector's own getAllJobs() call and
        //  IJobIndex build -- deliberately, not drift. buildParams() awaits
        //  this.jobsReady, and if that instead waited on the child's @ready
        //  event, a getAllJobs() failure would mean the child self-hides and
        //  never emits ready, so the await would hang forever instead of
        //  degrading to exact-class matching. getAllJobs() is cached and
        //  request-deduped, so this second call costs nothing.
        async loadJobs() {
            try {
                const jobs = await JobProvider.getAllJobs();
                this.jobIndex = buildJobIndex(jobs);
            } catch (e) {
                //  Not fatal: without the hierarchy the class filter simply
                //  cannot add ancestors, and every other filter still works.
                console.error('Failed to load job hierarchy for skill search', e);
            }
        },
        pushUrl() {
            const q = skillFilterToQuery(this.filter);
            this.lastPushedQuery = canonicalQuery(q);
            this.$router.replace({ ...this.$route, query: q });
        },
        onInput(value: string) {
            //  Trimmed here so filter.name, the URL and the request all agree,
            //  and the frame's echo guard strips the space from the box too.
            this.filter.name = value.trim();
            this.pushUrl();
            this.search();
        },
        onSort(key: SkillSortKey) {
            this.filter.sort = key;
            this.filter.dir = defaultDir(key);
            this.pushUrl();
            //  Sorting is client-side; no refetch.
        },
        onDir(dir: SortDir) {
            this.filter.dir = dir;
            this.pushUrl();
        },
        onRetry() {
            this.search();
        },
        onListSelect(key: SkillFilterListKey, ids: number[]) {
            this.filter[key] = ids;
            this.pushUrl();
            this.search();
        },
        toggleElement(id: number) {
            const set = new Set(this.filter.elements);
            if (set.has(id)) {
                set.delete(id);
            } else {
                set.add(id);
            }
            this.onListSelect('elements', [...set].sort((a, b) => a - b));
        },
        onInheritChange(on: boolean) {
            this.filter.inheritJobs = on;
            this.pushUrl();
            //  Changes which job ids go on the wire, so this one does refetch.
            this.search();
        },
        onClassMeta(meta: { parents: Parent[]; labels: Record<number, string> }) {
            this.jobParents = meta.parents;
            this.jobLabels = meta.labels;
        },
        //  Dispatches on chip.group, not chip.removeIds: the "Exact class
        //  only" chip carries removeIds: [], so a generic remove-by-ids
        //  handler would make its X do nothing.
        onRemoveChip(chip: IFilterChip) {
            if (chip.group === 'inherit') {
                this.filter.inheritJobs = true;
            } else {
                const key = chip.group as SkillFilterListKey;
                const remove = new Set(chip.removeIds);
                this.filter[key] = this.filter[key].filter((id) => !remove.has(id));
            }
            this.pushUrl();
            this.search();
        },
        onClearChips() {
            const f = this.filter;
            f.jobs = [];
            f.inheritJobs = true;
            f.skillTypes = [];
            f.durationTypes = [];
            f.targetTypes = [];
            f.elements = [];
            f.weapons = [];
            f.groups = [];
            f.skillGroups = [];
            f.globalSkillGroups = [];
            this.pushUrl();
            this.search();
        },
        async buildParams(): Promise<ISkillSearchParams> {
            const f = this.filter;
            let jobs = f.jobs;
            if (jobs.length && f.inheritJobs) {
                if (this.jobsReady) {
                    await this.jobsReady;
                }
                jobs = withAncestors(jobs, this.jobIndex);
            }
            return {
                name: f.name.trim() || undefined,
                job: jobs,
                weapon: f.weapons,
                skillType: f.skillTypes,
                durationType: f.durationTypes,
                targetType: f.targetTypes,
                element: f.elements,
                group: f.groups,
                skillGroup: f.skillGroups,
                globalSkillGroup: f.globalSkillGroups,
            };
        },
        async search() {
            if (!this.hasQuery) {
                this.loadSeq++;
                this.skills = [];
                this.error = '';
                this.loading = false;
                return;
            }

            const seq = ++this.loadSeq;
            this.loading = true;
            this.error = '';

            try {
                const trimmed = this.filter.name.trim();
                let results: ISkill[];
                //  Digits-only rather than Number()/isNaN(): the latter also
                //  accepts "12.5", "-5" and "1e5", and there is no such thing
                //  as a decimal or negative id in this data. Matches
                //  ItemSearchEx and QuickNav.
                //
                //  Gated on there being NO active filter: an id lookup ignores
                //  every filter param, so honouring the shortcut while filters
                //  are set would silently show a result the filters exclude.
                if (/^\d+$/.test(trimmed) && !isSkillFilterActive(this.filter)) {
                    //  getSkill REJECTS on a missing id (unlike
                    //  ItemsProvider.getStub, which resolves null), so a 404
                    //  here means "no skill with that id" -- an empty result,
                    //  not a failed search. Swallow only that; a 500, a
                    //  network drop or anything else is a real error and must
                    //  still reach the error state.
                    let sk: ISkill | null = null;
                    try {
                        sk = await SkillProvider.getSkill(Number(trimmed));
                    } catch (e) {
                        if ((e as any)?.response?.status !== 404) {
                            throw e;
                        }
                    }
                    results = sk ? [sk] : [];
                } else {
                    const params = await this.buildParams();
                    if (seq !== this.loadSeq) {
                        return;
                    }
                    results = await SkillProvider.searchSkills(params);
                }
                if (seq !== this.loadSeq) {
                    return;
                }
                this.skills = results;
            } catch (e) {
                if (seq !== this.loadSeq) {
                    return;
                }
                this.error = String(e);
                this.skills = [];
            } finally {
                if (seq === this.loadSeq) {
                    this.loading = false;
                }
            }
        },
    },
});
